import React, { createContext, useContext, useMemo } from "react";
import { useAuth } from "./AuthContext";

const TIER_ORDER = ["free", "starter", "professional", "team", "enterprise", "institutional"];

const FEATURE_MAP = {
  portfolio_overview: ["free", "starter", "professional", "team", "enterprise", "institutional"],
  ingestion_free: ["free", "starter", "professional", "team", "enterprise", "institutional"],

  strength_profile: ["starter", "professional", "team", "enterprise", "institutional"],
  fhi: ["starter", "professional", "team", "enterprise", "institutional"],
  ingestion: ["starter", "professional", "team", "enterprise", "institutional"],
  early_warnings: ["starter", "professional", "team", "enterprise", "institutional"],
  variance: ["starter", "professional", "team", "enterprise", "institutional"],

  bsp: ["professional", "team", "enterprise", "institutional"],
  benchmarks: ["professional", "team", "enterprise", "institutional"],
  covenants: ["professional", "team", "enterprise", "institutional"],
  unit_economics: ["professional", "team", "enterprise", "institutional"],
  scenarios: ["professional", "team", "enterprise", "institutional"],
  sensitivity: ["professional", "team", "enterprise", "institutional"],
  forecasting: ["professional", "team", "enterprise", "institutional"],
  macro: ["professional", "team", "enterprise", "institutional"],
  esg: ["professional", "team", "enterprise", "institutional"],
  reports: ["professional", "team", "enterprise", "institutional"],
  ai_insights: ["professional", "team", "enterprise", "institutional"],

  review_workflows: ["team", "enterprise", "institutional"],
  team_admin: ["team", "enterprise", "institutional"],
  audit_trail: ["team", "enterprise", "institutional"],
  report_builder: ["team", "enterprise", "institutional"],

  goal_seek: ["enterprise", "institutional"],
  investment_appraisal: ["enterprise", "institutional"],
  cost_benefit: ["enterprise", "institutional"],
  monte_carlo: ["enterprise", "institutional"],
  api_integrations: ["enterprise", "institutional"],
  advanced_esg: ["enterprise", "institutional"],
  advanced_forecasting: ["enterprise", "institutional"],
  advanced_macro: ["enterprise", "institutional"],

  multi_portfolio: ["institutional"],
  country_risk: ["institutional"],
  custom_models: ["institutional"],
};

const PREVIEW_FEATURES = {
  strength_profile_preview: ["free"],
  fhi_preview: ["free"],
  macro_preview: ["free"],
};

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();

  const role = String(user?.role || "viewer").trim().toLowerCase();
  const isOwner = role === "owner";
  const isAdmin = role === "admin";
  const isBillingAdmin = role === "billing_admin";
  const isReviewer = role === "reviewer";
  const isAnalyst = role === "analyst";
  const isTester = role === "tester";

  // Internal means SignalIQ-facing users who can use product modules.
  // Tester is intentionally included for beta access, but NOT treated as admin.
  const isInternalRole = isOwner || isAdmin || isReviewer || isAnalyst || isTester;

  // Beta testers should experience the product as unlocked, while their stored
  // billing tier can remain "free" for commercial/subscription reporting.
  const accountPlan = String(user?.tier || "free").trim().toLowerCase();
  const effectivePlan = isOwner || isAdmin || isTester ? "institutional" : accountPlan;
  const plan = isTester ? "beta" : effectivePlan;

  const hasFeature = (featureKey) => {
    if (isOwner || isAdmin || isTester) return true;
    const allowedTiers = FEATURE_MAP[featureKey];
    if (!allowedTiers) return true;
    return allowedTiers.includes(effectivePlan);
  };

  const hasPreview = (featureKey) => {
    if (isOwner || isAdmin || isTester) return false;
    const allowedTiers = PREVIEW_FEATURES[featureKey];
    if (!allowedTiers) return false;
    return allowedTiers.includes(effectivePlan);
  };

  const hasMinTier = (minTier) => {
    if (isOwner || isAdmin || isTester) return true;
    return TIER_ORDER.indexOf(effectivePlan) >= TIER_ORDER.indexOf(minTier);
  };

  const upgradePromptTier = (featureKey) => {
    const allowed = FEATURE_MAP[featureKey];
    if (!allowed || allowed.length === 0) return null;
    return allowed[0];
  };

  // Governance/admin-style areas remain restricted. Testers can use product
  // modules, but should not see model governance, admin, billing, or workforce controls.
  const canViewGovernance = (isOwner || isAdmin || isReviewer) && hasMinTier("team");
  const canViewEmployeeSync = (isOwner || isAdmin || isReviewer) && hasMinTier("team");

  const canViewCompanyAnalysis = (company) => {
    if (!company) return false;
    if (isOwner || isAdmin || isTester) return true;

    const visibility = company.visibility || "private";
    const ownerUserId = company.owner_user_id ?? null;
    const ownerOrgId = company.owner_org_id ?? null;
    const currentUserId = user?.user_id ?? null;
    const currentOrgId = user?.org_id ?? null;
    const allowPublicAnalysis = Boolean(company.allow_public_analysis);

    if (visibility === "public" && allowPublicAnalysis) return true;
    if (ownerUserId && currentUserId && ownerUserId === currentUserId) return true;
    if (ownerOrgId && currentOrgId && ownerOrgId === currentOrgId) return true;

    return false;
  };

  const value = useMemo(
    () => ({
      plan,
      role,
      isOwner,
      isAdmin,
      isBillingAdmin,
      isReviewer,
      isAnalyst,
      isTester,
      isInternalRole,
      effectivePlan,
      accountPlan,
      hasFeature,
      hasPreview,
      hasMinTier,
      upgradePromptTier,
      canViewGovernance,
      canViewEmployeeSync,
      canViewCompanyAnalysis,
      TIER_ORDER,
    }),
    [plan, effectivePlan, accountPlan, role, isOwner, isAdmin, isBillingAdmin, isReviewer, isAnalyst, isTester, isInternalRole, user]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscription = () => useContext(SubscriptionContext);
